import { Navigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Building2 } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/* Honest gate instead of a silent redirect: a buyer opening /dashboard/*
   learns exactly what is missing and how to get it (QA S2 finding). */
const OrganizerGate = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="organizer-gate">
      <div className="max-w-md text-center flex flex-col items-center gap-5 py-24">
        <div className="w-14 h-14 rounded-2xl bg-[#1D4D3B]/10 flex items-center justify-center">
          <Building2 className="w-6 h-6 text-[#1D4D3B]" aria-hidden="true" />
        </div>
        <h1 className="text-2xl font-light tracking-tight text-[#0A0A0A]">{t("gate.organizerTitle")}</h1>
        <p className="text-sm text-[#737373] leading-relaxed">{t("gate.organizerBody")}</p>
        <div className="flex items-center gap-3">
          <Button asChild data-testid="organizer-gate-cta" className="rounded-full h-11 px-6 bg-[#0A0A0A] text-white hover:bg-black/80">
            <Link to="/create">
              {t("gate.organizerCta")}
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </Link>
          </Button>
          <Button asChild variant="ghost" data-testid="organizer-gate-home" className="rounded-full h-11 px-5">
            <Link to="/">{t("gate.backHome")}</Link>
          </Button>
        </div>
        {/* gate renders outside any shell — bring the switcher along (QA it30) */}
        <LanguageSwitcher />
      </div>
    </div>
  );
};

export const ProtectedRoute = ({ children, roles }) => {
  const { user } = useAuth();
  const location = useLocation();

  if (user === undefined) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 space-y-6" data-testid="route-guard-loading">
        <Skeleton className="h-12 w-1/3 rounded-xl" />
        <Skeleton className="h-64 w-full rounded-3xl" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  }

  if (roles && !roles.includes(user.role)) {
    // a signed-in buyer heading to the organizer dashboard gets guidance, not a bounce
    if (roles.includes("organizer") && user.role === "buyer") {
      return <OrganizerGate />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};
